import {
  useState,
  useCallback,
  useEffect,
  useRef,
  SetStateAction,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Body,
  ContentContainer,
  Header,
  NavigateBeforeButton,
  SearchHeader,
} from "@components";
import {
  SearchBar,
  MostPopularSearches as MostPopularSearchesBase,
} from "@components/Search";
import { Procedure, RequestType, Suggestion } from "@interfaces";
import { minimumSearchLength, mostSearchedProceduresQueries } from "@constants";
import { parseProcedureSuggestions } from "./utils/parse";
import { Box, styled, Theme, Typography, useMediaQuery } from "@mui/material";
import { useDebouncedCallback } from "@hooks/useDebouncedCallback";
import { getMedicalProceduresSuggestions } from "@services";

const StyledBody = styled(Body)(({ theme }) => ({
  flex: 1,
  gap: 24,
}));

const StyledContainer = styled(ContentContainer)({
  flex: 1,
  justifyContent: "center",
});

const DesktopSearchBar = styled(SearchBar)({
  position: "static",
  maxWidth: "100%",
});

const DesktopPopularSearchesContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  justifyContent: "left",
  width: "100%",
}));

const MostPopularSearches = styled(MostPopularSearchesBase)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    width: "100vw",
    position: "absolute",
    top: 64,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
    height: "100%",
  },
}));

const MostPopularSearchesTitle = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

interface LocationState {
  attachedProcedures?: Suggestion[];
  medicalOrderStarted?: boolean;
}

export const SearchProcedure = () => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const {
    attachedProcedures = [],
    medicalOrderStarted = false,
  }: LocationState = location.state || {};
  const { requestId = "" } = useParams();

  const [searchText, setSearchText] = useState<string>("");

  const prevSearchText = useRef<string>("");

  const [searching, setSearching] = useState<boolean>(false);

  const [errorFetchingResults, setErrorFetchingResults] =
    useState<boolean>(false);
  const [preliminaryResults, setPreliminaryResults] = useState<Suggestion[]>(
    []
  );

  const [proceduresResults, setProceduresResults] = useState<Procedure[]>([]);

  const wrappedSetSearchText = useCallback((value: SetStateAction<string>) => {
    setSearchText(value);
    setErrorFetchingResults(false);
  }, []);

  const search = useDebouncedCallback(async (text: string) => {
    prevSearchText.current = text;
    if (!text || text.trim().length < minimumSearchLength) return;
    setSearching(true);
    const filteredSearchText = text.trim().replace(/\bde\b/g, "");
    try {
      const params = new URLSearchParams();
      params.set("query", filteredSearchText.toLowerCase());

      const suggestions = await getMedicalProceduresSuggestions(
        filteredSearchText
      );

      const proceduresSuggestions = suggestions.hits.map(
        ({ code, description, group, subgroup }) =>
          ({
            code,
            description,
            group,
            subgroup,
          } as Procedure)
      );
      setProceduresResults(proceduresSuggestions);

      const parsedSuggestions = proceduresSuggestions.map((procedure) => ({
        text: parseProcedureSuggestions(procedure),
        code: procedure.code,
      }));
      setPreliminaryResults(parsedSuggestions);
    } catch (error) {
      console.log(error);
      setErrorFetchingResults(true);
      setPreliminaryResults([]);
    }
    setSearching(false);
  }, 500);

  const handleSearch = useCallback(async () => {
    void search(searchText);
  }, [search, searchText]);

  const handleBack = useCallback(() => {
    if (medicalOrderStarted) {
      navigate(`/app/solicitudes/${requestId}/orden-medica/crear`, {
        state: { repeatedDocumentDetails: attachedProcedures },
      });
    } else {
      navigate(`/app/nuevo-documento/paciente`, {
        state: {
          documentType: RequestType.MEDICAL_ORDER,
        },
      });
    }
  }, [attachedProcedures, medicalOrderStarted, navigate, requestId]);

  const onPreliminarySearchItemClick = useCallback(
    (item: Suggestion) => {
      let procedure: Procedure | undefined;

      const emptySearchText = searchText.length === 0;
      // Clicking on a most searched procedure suggestion
      if (emptySearchText) {
        const procedureSuggestion = mostSearchedProceduresQueries.find(
          (commonResult) => item.text === commonResult.text
        );
        if (procedureSuggestion) {
          procedure = { ...procedureSuggestion } as Procedure;
        }
      } else {
        // Clicking on a searched procedure suggestion
        procedure = proceduresResults.find(
          (procedure) => item.text === parseProcedureSuggestions(procedure)
        );
      }

      if (procedure) {
        const existingProcedure = procedure; // TODO: Delete this variable, it is necesary to typescript
        const isProcedureAttached = attachedProcedures.some(
          (attachedProcedure: Suggestion) =>
            attachedProcedure.text ===
            parseProcedureSuggestions(existingProcedure)
        );

        const newAttachedProcedures = !isProcedureAttached
          ? [
              ...attachedProcedures,
              {
                text: parseProcedureSuggestions(existingProcedure),
                code: existingProcedure.code,
              },
            ]
          : attachedProcedures;

        navigate(`/app/solicitudes/${requestId}/orden-medica/crear`, {
          state: { repeatedDocumentDetails: newAttachedProcedures },
        });
      }
    },
    [
      attachedProcedures,
      navigate,
      proceduresResults,
      requestId,
      searchText.length,
    ]
  );

  const [prevScrollPos, setPrevScrollPos] = useState(0);

  useEffect(() => {
    if (searchText.trim().length < minimumSearchLength) {
      setPreliminaryResults([]);
    }
  }, [searchText]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      if (currentScrollPos > prevScrollPos) {
        // Remove focus from input (to hide keyboard on mobile)
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }
      setPrevScrollPos(currentScrollPos);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [prevScrollPos]);

  const showAddManuallyItemButton =
    (!searching &&
      prevSearchText.current === searchText.trim() &&
      searchText.length > 0) ||
    preliminaryResults.length > 0;

  const showEmptyResultsMessage =
    searchText.length >= minimumSearchLength &&
    !searching &&
    !errorFetchingResults &&
    prevSearchText.current === searchText.trim() &&
    preliminaryResults.length === 0;

  const showErrorResultsMessage = errorFetchingResults && !searching;

  const showLoader =
    searching &&
    preliminaryResults.length === 0 &&
    searchText.length >= minimumSearchLength;

  return (
    <>
      {!isDesktop ? (
        <SearchHeader
          searchText={searchText}
          setSearchText={wrappedSetSearchText}
          onPreliminaryItemClick={onPreliminarySearchItemClick}
          preliminaryResults={preliminaryResults}
          search={handleSearch}
          handleBack={handleBack}
          locationState={{ attachedProcedures }}
          placeholder="Busca examen o procedimiento"
          elevation={1}
          addManuallyItemText="Crear procedimiento manualmente"
          showAddManuallyItemButton={showAddManuallyItemButton}
          showEmptyResultsMessage={showEmptyResultsMessage}
          showErrorResultsMessage={showErrorResultsMessage}
          showLoader={showLoader}
        />
      ) : (
        <Header
          useMekiDocIcon={isDesktop}
          left={<NavigateBeforeButton onClick={handleBack} />}
        />
      )}
      <StyledContainer disableGutters>
        <StyledBody>
          {isDesktop && (
            <>
              <Typography variant="h5">Exámenes o procedimientos</Typography>
              <DesktopSearchBar
                searchText={searchText}
                setSearchText={wrappedSetSearchText}
                onPreliminaryItemClick={onPreliminarySearchItemClick}
                preliminaryResults={preliminaryResults}
                search={handleSearch}
                locationState={{ attachedProcedures }}
                placeholder="Busca examen o procedimiento"
                addManuallyItemText="Crear procedimiento manualmente"
                showAddManuallyItemButton={showAddManuallyItemButton}
                showEmptyResultsMessage={showEmptyResultsMessage}
                showErrorResultsMessage={showErrorResultsMessage}
                showLoader={showLoader}
              />
            </>
          )}
          {searchText.length === 0 && (
            <>
              <DesktopPopularSearchesContainer>
                <MostPopularSearchesTitle variant="body1">
                  Búsquedas Populares
                </MostPopularSearchesTitle>
                <MostPopularSearches
                  onItemClick={onPreliminarySearchItemClick}
                  mostSearchedQueries={mostSearchedProceduresQueries}
                />
              </DesktopPopularSearchesContainer>
            </>
          )}
        </StyledBody>
      </StyledContainer>
    </>
  );
};
